import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SINGUP_API } from "../../hooks/APIHooks";
import { FaEye, FaEyeSlash, FaUser, FaPhone, FaKey, FaUserPlus } from "react-icons/fa";
import { GlobalData } from "../../assets/data/GlobalData";
import { useLocation } from "react-router-dom";
import { Dashboard_Profile } from "../../assets/data/Imagedata";

const Signup = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [referenceBy, setReferenceBy] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get("ref");
    if (refCode) {
      setReferenceBy(refCode);
    }
  }, [location.search]);

  const validatePhoneNumber = (phone) => {
    // Only allow digits and between 10 and 12 characters
    const phoneRegex = /^\d{10,12}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    if (/^\d*$/.test(value)) {
      setPhoneNumber(value);
      // Clear phone error when user types
      if (errors.phoneNumber) {
        setErrors(prev => ({ ...prev, phoneNumber: "" }));
      }
    }
  };

  const validateFields = () => {
    let newErrors = {};
    let isValid = true;

    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required";
      isValid = false;
    }
    
    if (!phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
      isValid = false;
    } else if (!validatePhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be between digits";
      isValid = false;
    }
    
    if (!referenceBy.trim()) {
      newErrors.referenceBy = "Reference code is required";
      isValid = false;
    }
    
    if (!password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (password.length <= 6) {
      newErrors.password = "Password must be more than 6 characters";
      isValid = false;
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const onSignUpPress = async () => {
    if (!validateFields()) return;
    setError("");
    setLoading(true);
    try {
      const response = await fetch(SINGUP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          referenceBy: referenceBy,
          phonenumber: phoneNumber,
          sex: "Male",
          role: "customer",
          password: password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === "success") {
        navigate("/");
      } else {
        setError(
          data.message || `Failed to sign up. Status code: ${response.status}`
        );
      }
    } catch (err) {
      console.error("Sign-up error", err);
      setError("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSignUpPress();
    }
  };

  return (
    <section className="min-h-screen bg-primary-100  flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card Container */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-primary  p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <img
                  src={Dashboard_Profile}
                  alt="Dashboard Profile"
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg"
                />
                <div className="absolute -bottom-2 -right-2 bg-yellow-500 w-10 h-10 rounded-full flex items-center justify-center">
                  <FaUserPlus className="text-white text-lg" />
                </div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white">
              {GlobalData.company.companyName}
            </h1>
            <p className="text-white text-sm mt-1">
              {GlobalData.company.companyTagLine}
            </p>
            <h2 className="text-xl font-semibold text-white mt-4">Create Account</h2>
          </div>

          {/* Form */}
          <div className="p-6 md:p-8">
            {/* Full Name */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <FaUser className="mr-2 text-primary" />
                Full Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={fullName}
                  placeholder="Enter your full name"
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (errors.fullName) setErrors(prev => ({ ...prev, fullName: "" }));
                  }}
                  onKeyPress={handleKeyPress}
                  className={`w-full px-4 py-3 pl-10 border rounded-lg focus:outline-none focus:ring-2  transition-all duration-300 ${
                    errors.fullName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <FaUser className="absolute left-3 top-3.5 text-gray-400" />
              </div>
              {errors.fullName && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <span className="mr-1">⚠</span> {errors.fullName}
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <FaPhone className="mr-2 text-primary" />
                Phone Number
              </label>
              <div className="relative">
                <input
                  type="tel"
                  value={phoneNumber}
                  placeholder="Enter 10-digit phone number"
                  onChange={handlePhoneNumberChange}
                  onKeyPress={handleKeyPress}
                  maxLength={12}
                  className={`w-full px-4 py-3 pl-10 border rounded-lg focus:outline-none focus:ring-2  transition-all duration-300 ${
                    errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <FaPhone className="absolute left-3 top-3.5 text-gray-400" />
                {phoneNumber && (
                  <span className="absolute right-3 top-3.5 text-sm text-gray-500">
                    {phoneNumber.length}/12
                  </span>
                )}
              </div>
              {errors.phoneNumber && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <span className="mr-1">⚠</span> {errors.phoneNumber}
                </p>
              )}
            </div>

            {/* Reference Code */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <FaUserPlus className="mr-2 text-primary" />
                Reference Code
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={referenceBy}
                  placeholder="Enter reference code"
                  onChange={(e) => {
                    setReferenceBy(e.target.value);
                    if (errors.referenceBy) setErrors(prev => ({ ...prev, referenceBy: "" }));
                  }}
                  onKeyPress={handleKeyPress}
                  className={`w-full px-4 py-3 pl-10 border rounded-lg focus:outline-none focus:ring-2  transition-all duration-300 ${
                    errors.referenceBy ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <FaUserPlus className="absolute left-3 top-3.5 text-gray-400" />
              </div>
              {errors.referenceBy && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <span className="mr-1">⚠</span> {errors.referenceBy}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <FaKey className="mr-2 text-primary" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  placeholder="At least 6 characters"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
                  }}
                  onKeyPress={handleKeyPress}
                  className={`w-full px-4 py-3 pl-10 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <FaKey className="absolute left-3 top-3.5 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3.5 text-gray-500 hover:text-primary focus:outline-none transition-colors duration-300"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <span className="mr-1">⚠</span> {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="mb-8">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <FaKey className="mr-2 text-primary" />
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  placeholder="Confirm your password"
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: "" }));
                  }}
                  onKeyPress={handleKeyPress}
                  className={`w-full px-4 py-3 pl-10 pr-12 border rounded-lg focus:outline-none focus:ring-2  transition-all duration-300 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <FaKey className="absolute left-3 top-3.5 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3.5 text-gray-500 hover:text-primary focus:outline-none transition-colors duration-300"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <span className="mr-1">⚠</span> {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Sign Up Button */}
            <button
              onClick={onSignUpPress}
              onKeyPress={handleKeyPress}
              disabled={loading}
              className={`w-full py-3.5 rounded-lg font-semibold text-white transition-all duration-300 transform hover:scale-[1.02] ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-primary  hover:bg-primary-100  shadow-lg'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Account...
                </span>
              ) : (
                "Sign Up"
              )}
            </button>

            {/* Server Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Login Link */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-gray-600">
                Already have an account?{" "}
                <Link
                  to="/"
                  className="text-primary font-semibold hover:text-primary-100 transition-colors duration-300"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Terms & Privacy */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            By signing up, you agree to our{" "}
            <a href="#" className="text-white hover:underline">Terms</a> and{" "}
            <a href="#" className="text-white hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Signup;